import type { FC, PropsWithChildren } from 'react';
import { useEffect, useMemo } from 'react';

import { Outlet, useLocation, useUnsafeNavigate } from '@luna/router';

import { createNavigationMap } from './createNavigationMap';
import { NavigationProvider, useNavigation } from './NavigationContext';
import type { Navigation } from './typing';

type NavigationPath<Path> = Path extends string ? Path | 'close' : 'close';

type FeatureNavigationParams<Path, DefaultExitUrl> = {
    defaultExitUrl?: DefaultExitUrl;
    navigations?: NavigationPath<Path>[];
};

type NavigationContextProps<DefaultExitUrl> = DefaultExitUrl extends string
    ? object
    : { featureCloseNavigation: Navigation };

type FeatureNavigation<Path, DefaultExitUrl> = [
    NavigationContext: FC<PropsWithChildren<NavigationContextProps<DefaultExitUrl>>>,
    useNavigation: typeof useNavigation<NavigationPath<Path>>,
];

export const createFeatureNavigation = <Path, DefaultExitUrl>({
    defaultExitUrl,
    navigations = [],
}: FeatureNavigationParams<Path, DefaultExitUrl>): FeatureNavigation<Path, DefaultExitUrl> => {
    const FeatureNavigationProvider: FC<
        PropsWithChildren<NavigationContextProps<DefaultExitUrl>>
    > = ({ children = <Outlet />, ...props }) => {
        const navigate = useUnsafeNavigate();
        const { state = {} } = useLocation();

        const NavigateComponent: Parameters<typeof createNavigationMap>[0]['navigateComponent'] = ({
            to,
            ...options
        }) => {
            useEffect(() => {
                navigate(to, options);
            });

            return null;
        };

        const navigation = useMemo(() => {
            const originUrl = (state as Record<string, string>)?.origin || defaultExitUrl || '/';

            const closeNavigation: Navigation = defaultExitUrl
                ? // @ts-expect-error invalid type
                  [() => navigate(originUrl), () => <NavigateComponent to={originUrl as string} />]
                : (props as NavigationContextProps<undefined>).featureCloseNavigation;

            return {
                ...createNavigationMap(
                    {
                        navigate,
                        navigateComponent: NavigateComponent,
                    },
                    ...navigations,
                ),
                close: closeNavigation,
            };
        }, []);

        return <NavigationProvider value={navigation}>{children}</NavigationProvider>;
    };

    return [FeatureNavigationProvider, useNavigation];
};
